import classNames from 'classnames';
import * as React from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

import { Error } from 'components/error/Error';
import Form from 'components/form/Form';
import Grid from 'components/grid/Grid';
import { Loader } from 'components/loader/Loader';
import { STATUSES } from 'core/api';
import { IReduxStoreState } from 'core/store/interfaces';

import { formConfig, gridConfig, decorators } from './config';
import { ICategory, IProps } from './interfaces';
import {
  createCategory,
  deleteCategory,
  editCategory,
  fetchCategories,
  fetchCategoryInfo,
  resetCategoryInfo,
} from './redux';
import { withModals } from 'core/modals/ModalsProvider';
import { IWithModalProps } from 'core/modals/interfaces';
import styles from './Categories.module.styl';
import { FormLoader } from '../../../components/form/FormLoader';
import { MODALS } from 'core/modals/config';

const initialValues = {
  title: '',
  slug: '',
  isPublished: false,
  isSlugNotSynchronized: false,
  description: null,
  permission: {
    countries: null,
    departments: null,
    seniorities: null,
    teams: null,
    titles: null,
    workplaces: null,
  },
};

class Categories extends React.Component<IProps & IWithModalProps> {
  public componentDidMount(): void {
    const {
      location: { search, hash },
      match: {
        params: { id },
      },
    } = this.props;

    if (id) {
      this.props.fetchCategoryInfo(id);
    }

    this.props.fetchCategories({
      hash,
      search,
    });
  }

  public componentDidUpdate = (prevProps: IProps) => {
    const {
      location: { search, hash },
      match: {
        params: { id },
      },
    } = this.props;

    if (
      search !== prevProps.location.search ||
      hash !== prevProps.location.hash
    ) {
      this.props.fetchCategories({
        hash,
        search,
      });
    }

    if (id === prevProps.match.params.id) {
      return;
    }

    if (!id) {
      this.props.resetCategoryInfo();
    } else {
      this.props.fetchCategoryInfo(id);
    }
  };

  public componentWillUnmount() {
    this.props.resetCategoryInfo();
  }

  public render() {
    const {
      match: {
        params: { id },
      },
    } = this.props;

    return (
      <div className={classNames(styles.root, id && styles.fullSize)}>
        {this.getRenderedForm()}
        {!id && this.getRenderedList()}
      </div>
    );
  }

  private readonly getRenderedForm = () => {
    const {
      categoryInfo: {
        error,
        status,
        data: { data: categoryInfo },
      },
    } = this.props;

    switch (status) {
      case STATUSES.REQUEST:
        return <FormLoader config={formConfig} />;
      case STATUSES.FAILED:
        return <Error>{error}</Error>;
      case STATUSES.INITIAL:
      case STATUSES.SUCCESS:
        return (
          <Form
            key={categoryInfo ? 'update' : 'create'}
            config={formConfig}
            onSubmit={this.onSubmit}
            validate={this.validate}
            initialValues={categoryInfo || initialValues}
            withTranslation="category"
            decorators={decorators(categoryInfo || initialValues)}
          />
        );
      default:
        return null;
    }
  };

  private getRenderedList() {
    const {
      categoriesList,
      location: { hash },
    } = this.props;

    const searchPhrase = hash ? hash.split('#')[1] : '';

    switch (categoriesList.status) {
      case STATUSES.REQUEST:
        return <Loader />;
      case STATUSES.FAILED:
        return <Error>{categoriesList.error}</Error>;
      case STATUSES.SUCCESS:
        return (
          <Grid
            data={categoriesList.data.items}
            config={gridConfig}
            pagination={categoriesList.data.pagination}
            urlPrefix="/admin/categories"
            removeItem={this.removeCategory}
            onSearch={this.handleSearch}
            searchPhrase={searchPhrase}
          />
        );
      default:
        return null;
    }
  }

  private readonly handleSearch = (searchPhrase: string) => {
    const { history } = this.props;

    history.push(`/admin/categories#${searchPhrase}`);
  };

  private readonly onSubmit = async (values: ICategory, onSuccess: any) => {
    const {
      history,
      location: { hash, search },
      match: {
        params: { id },
      },
    } = this.props;

    if (!id) {
      await this.props.createCategory(values);
    } else {
      await this.props.editCategory(id, values);
    }

    this.props.resetCategoryInfo();
    onSuccess();
    history.push('/admin/categories');

    this.props.fetchCategories({
      hash,
      search,
    });

    return true;
  };

  private readonly removeCategory = async (id: string) => {
    const {
      location: { hash, search },
      openModal,
    } = this.props;
    openModal(MODALS.CONFIRM, {
      caption: 'Warning',
      onConfirm: async () => {
        try {
          await this.props.deleteCategory(id);
          this.props.fetchCategories({
            hash,
            search,
          });
          toast.success('Success!');
        } catch (error) {
          const { message } = error as Error;

          toast.error(message);
        }
      },
      text: 'Are you sure you want to delete this category?',
    });
    return;
  };

  private readonly validate = async (values: any) => {
    const errors: Record<string, string> = {};

    const requiredFields = formConfig.fields
      .flat()
      .filter((field: any) => field.isRequired);

    requiredFields.forEach((field: any) => {
      if (!values[field.name]) {
        errors[field.name] = 'Required Field';
      }
    });

    return errors;
  };
}

const mapState = (state: IReduxStoreState) => ({
  categoriesList: state.categories.list,
  categoryInfo: state.categories.item,
});

const mapDispatch = {
  createCategory,
  deleteCategory,
  editCategory,
  fetchCategories,
  fetchCategoryInfo,
  resetCategoryInfo,
};

export default connect(mapState, mapDispatch)(withModals<any, any>(Categories));
